<script setup>
import {computed, ref} from "vue";
import {useStore} from "vuex";

import DefaultButton from "@/components/onsiteEditor/components/buttons/DefaultButton.vue";
import {default as GlobalStyledButton} from "@/components/global-themed/buttons/DefaultButton.vue";
import useInstallation from "@/composables/optimizer/useOnsiteOptimizer";

const verificationInProgress = ref(false);
const verifiedMessage = ref(null);
const showOptimizerValidationError = ref(false);
const emit = defineEmits(['goToAssisted', 'back']);

const props = defineProps({
  isOnboarding: {
    type: Boolean,
    required: false,
    default: false,
  },
  usingGlobalStyling: { // used to use newer styling when embedding this via ai-onboarding flow
    type: Boolean,
    required: false,
    default: false,
  },
});

const store = useStore();

const {
  assistedInstallation,
  showInstallForMeButton,
} = useInstallation();

const buttonText = computed(() => {
  if (verifiedMessage.value) {
    return 'Launch Onsite Optimizer';
  }

  return verificationInProgress.value ? 'Verifying...' : 'Verify Installation'
});

const buttonAction = () => {
  if (props.isOnboarding) {
    verifyInstallation();
    return;
  }
  verifiedMessage.value ? window.location.href = '/onsite-optimizer' : verifyInstallation();
};

const verifyInstallation = async () => {
  try {
    verificationInProgress.value = true;
    const response = await store.dispatch(
      `onsiteEditorInstallation/verifyInstallation`,
      store.getters["user/user"].active_website?.id
    );
    verifiedMessage.value = response.data.message;
    showOptimizerValidationError.value = false;
    store.commit('onsiteEditorInstallation/setCanRequestInstall', false);
    store.commit('onsiteEditorInstallation/setOptimiserInstallationStatus', true);
  } catch (error) {
    verifiedMessage.value = null;
    await store.dispatch('onsiteEditorInstallation/setCanRequestInstall', true);
    if (error.response.status === 400) {
      verificationInProgress.value = false;
      showOptimizerValidationError.value = true;
      return;
    }
  }
  verificationInProgress.value = false;
};
</script>

<template>
  <div
    class="tw-flex tw-gap-5"
    :class="{
      'tw-items-center': !showOptimizerValidationError,
      'tw-flex-col tw-items-start': showOptimizerValidationError
    }"
  >
    <div
      v-if="!isOnboarding || (isOnboarding && !verifiedMessage)"
      class="tw-flex tw-w-full tw-items-center tw-gap-4"
    >
      <GlobalStyledButton
        v-if="usingGlobalStyling"
        variant="primary-inverted"
        @click="emit('back')"
      >
        Back
      </GlobalStyledButton>

      <GlobalStyledButton
        v-if="showInstallForMeButton && usingGlobalStyling"
        variant="primary-inverted"
        @click="assistedInstallation(emit)"
      >
        I want you to install it for me
      </GlobalStyledButton>

      <DefaultButton
        v-if="showInstallForMeButton && !usingGlobalStyling"
        class="!tw-w-[280px]"
        variant="base"
        @click="assistedInstallation(emit)"
      >
        I want you to install it for me
      </DefaultButton>

      <GlobalStyledButton
        v-if="usingGlobalStyling"
        :disabled="verificationInProgress"
        @click="buttonAction"
      >
        {{ buttonText }}
      </GlobalStyledButton>

      <DefaultButton
        v-else
        :disabled="verificationInProgress"
        class="!tw-w-auto"
        @click="buttonAction"
      >
        {{ buttonText }}
      </DefaultButton>
    </div>

    <div
      v-if="showOptimizerValidationError"
      class="tw-flex tw-p-1.5 tw-text-denali"
    >
      <div class="tw-mr-2 tw-w-6 tw-shrink-0">
        <SvgVue
          class="tw-cursor-pointer tw-justify-between tw-rounded-full tw-bg-denali tw-p-[5px] tw-text-white"
          icon="icons/cross"
        />
      </div>
      <div>
        <p class="tw-text-denali">
          We were unable to verify the installation of the Onsite Optimizer. Please try again.
          Common reasons for verification failures include:
        </p>
        <ul class="tw-ml-8 tw-list-disc">
          <li class="tw-text-denali">
            <span class="tw-font-bold">Pagespeed Plugins:</span> Some pagespeed plugins (like WPRocket, NitroPack) delay
            or stop the loading of
            JavaScript, which can prevent our script from running. Ensure you exclude our script from these plugins. If
            you need help, please contact support.
          </li>
          <li class="tw-text-denali">
            <span class="tw-font-bold">Platform Caching:</span> Make sure you clear the cache for the website before
            verifying installation of the script.
          </li>
          <li class="tw-text-denali">
            <span class="tw-font-bold">Multiple Code Instances:</span> Adding the code to the website more than once can
            cause issues. Make sure the script is added only once.
          </li>
          <li class="tw-text-denali">
            <span class="tw-font-bold">Code Placement:</span> The code snippet must be placed within the &lt;head&gt;&lt;/head&gt;
            section of the website to function correctly.
          </li>
        </ul>
        <p class="tw-mt-2 tw-text-denali">
          Need assistance with installation? Let us handle it for you—just
          <span
            class="tw-cursor-pointer tw-underline"
            @click="emit('goToAssisted')"
          >
            click here
          </span>.
        </p>
      </div>
    </div>
    <div
      v-if="verifiedMessage"
      class="tw-flex tw-items-center tw-p-1.5 tw-text-emerald-400"
    >
      <SvgVue
        class="tw-mr-2 tw-h-8 tw-w-8 tw-cursor-pointer tw-justify-between tw-rounded-full tw-bg-emerald-400 tw-p-[5px] tw-text-white"
        icon="icons/check"
      />
      <div>{{ verifiedMessage }}</div>
    </div>
  </div>
</template>
